import { useEffect, useRef } from "react";

import { generatePurchaseOrder } from "./intakeApi";
import { useEntitlement } from "../contexts";

/**
 * Auto-surface the linked Sell Request's CONTRACTS documents the moment a
 * request is linked, created or changed — at ANY point in a flow (create
 * stock / consignment, edit):
 *
 *  - an agreement that was ALREADY generated (its *_agreement_sent_at is
 *    stamped) is fetched immediately: that only re-renders an existing
 *    document, no state changes server-side;
 *  - the agreement RELEVANT to this flow (purchasing for stock, consignment
 *    for consignments) is auto-GENERATED when the request carries a quote and
 *    the document does not exist yet. Generation stamps *_agreement_sent_at —
 *    deliberate (2026-09-04, Alex): the doc should exist without a click.
 *  - the Xero purchase order is auto-generated when the request has a quote
 *    AND a pinned Xero org and no PO yet. Without an org the endpoint is a
 *    guaranteed 422, so we surface why instead of calling it. A generated PO
 *    is a REAL purchase order in that Xero org. A store without Xero POs on
 *    its plan needs no org: the backend renders its own PO PDF instead.
 *
 * NOTHING here emails anybody: the send-purchase-order / send-*-agreement
 * endpoints are a separate, always-manual action and are never called.
 *
 * One attempt per request+document per mount — a failure shows its error and
 * falls back to the manual button rather than retrying in a loop. `request`
 * must be the FULL payload (GET /api/requests/{id}); the summary embedded in
 * a listing lacks last_quote and the Xero fields.
 */
export default function useAutoContractDocs({
  request,
  kind, // "purchasing" | "consignment" — which agreement this flow may generate
  agreements,
  loadAgreement,
  poPresent,
  onPo, // ({ ok, data?, message? }) => void
}) {
  const attempted = useRef({});
  const hasXeroPo = useEntitlement("xero_purchase_orders");

  useEffect(() => {
    if (!request?.id) return;
    const hasQuote = !!(request.last_quote?.price ?? request.quoted_price);

    ["purchasing", "consignment"].forEach((key) => {
      const sentAt =
        key === "purchasing"
          ? request.purchasing_agreement_sent_at
          : request.consignment_agreement_sent_at;
      const marker = `${request.id}:${key}`;
      const slot = agreements?.[key];
      if (attempted.current[marker] || slot?.url || slot?.loading || slot?.error) return;
      if (sentAt || (key === kind && hasQuote)) {
        attempted.current[marker] = true;
        loadAgreement(key);
      }
    });

    const poMarker = `${request.id}:po`;
    if (attempted.current[poMarker] || poPresent || !hasQuote) return;
    attempted.current[poMarker] = true;
    if (!request.xero_connection_id && hasXeroPo) {
      onPo({
        ok: false,
        message:
          "No Xero organisation is pinned on the linked Sell Request — choose one in the request's Payments section, then the purchase order can be generated.",
      });
      return;
    }
    generatePurchaseOrder(request.id)
      .then((data) => onPo({ ok: true, data }))
      .catch((e) =>
        onPo({
          ok: false,
          message:
            e?.response?.data?.message ||
            "The purchase order could not be generated automatically — generate it from the request flow.",
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    request?.id,
    request?.purchasing_agreement_sent_at,
    request?.consignment_agreement_sent_at,
    agreements,
    poPresent,
    kind,
  ]);
}
